import { PerformanceState } from './../../state/performance.state';
import {
  selectePerformanceClosingValue,
  selectePerformanceInceptionDates,
  selectPerformanceData,
  selectperformanceDataAndSettings,
  selectPerformanceDataErrorLoading,
  selectPerformanceSettings,
  selectPerformanceState,
  selectPerformanceStateLoading,
} from './../selectors/performance.selectors';
import {
  IPerformanceData,
  AssetPerformanceRequest,
  IKiwiSaverDashboardSummaryData,
  IPerformanceSnapshotObject,
  AccountSubType,
  IPerformanceBalancesObject,
  ReturnSymbol,
  DateSelectorCaption,
  ReturnTypes,
  ReturnSpanMonths,
} from './../../../models/performance';
import { Injectable } from '@angular/core';
import { Subscription, Observable, of, BehaviorSubject } from 'rxjs';
import { Store, select } from '@ngrx/store';
import { IAppState } from '../../state/app.state';
import { filter, switchMap, map, tap } from 'rxjs/operators';
import { IPerformance, PerformanceSettings } from 'src/app/models/performance';
import { AppConfig } from 'src/app/app.config';
import { Features } from 'src/app/models/features';
import {
  clearPerformanceState,
  loadAssetPerformanceData,
  loadPerformanceData,
  updatePerformanceSettings,
  updatePerformanceSettingsWithoutReload,
  loadAssetClassificationPerformanceData
} from '../actions/performance.actions';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { IGeneralSelectionState } from '../../state/general-selection.state';
import { ClientEntity } from 'src/app/models/clients';
import { GeneralSelectionFacade } from '../../general-selection/facade/general-selection.facade';
import { IPerformanceFeatureSettings } from 'src/app/models/featuresConfig';

@Injectable()
@UntilDestroy()
export class PerformanceFacade {
  portfoliosSubscription: Subscription;

  // Performance state
  private selectedReturnType = ReturnTypes.XIRR;
  private selectedReturnPeriod: string;
  private selectedReturnSpanMonths: number;
  // General Selection State
  private selectedDate: Date;
  private selectedEntities: ClientEntity[];
  
  breakDownData: any[];
  performanceData$: Observable<IPerformanceData> = this.store.pipe(
    select(selectPerformanceData)
  );
  performanceClosingValue$: Observable<number> = this.performanceData$.pipe(
    map((value: IPerformanceData) => value.closingValue)
  );

  public kiwiSaverDashboardSummaryWidgetData$: Observable<
    Partial<IKiwiSaverDashboardSummaryData>
  > = this.store.pipe(
    select(selectperformanceDataAndSettings),
    filter((data: Partial<PerformanceState>) => {
      return (
        Object.keys(data.performanceData).length !== 0 &&
        data.performanceData.constructor === Object
      );
      // TODO: Date selector. When date selector gets build in for KiwiSaver, this needs to be revisited so that the summary data gets updated even when the spanMonth value is not inception
      // The solution would be to create a new http service method only for the summary data that retrieves data since inception after a truthy response is returned when a method like 'checkToUpdatePerformanceData' is called
    }),
    switchMap((data: Partial<PerformanceState>) => {
      const performanceBalancesData: IPerformanceBalancesObject[] =
        data.performanceData.balances;
      const kiwiSaverDashboardSummaryWidgetData: Partial<IKiwiSaverDashboardSummaryData> =
        {
          loading: data.loading,
          retrieveData: data.retrieveData,
          currency: data.performanceData.currency,
          settings: data.settings,
          closingValue: {
            value: data.performanceData.closingValue,
            caption: 'Total',
          },
          yourContributions: {
            value: this.getAccountsBalancesValue(performanceBalancesData, [
              AccountSubType.KiwiSaverEmployeeContribution,
              AccountSubType.KiwiSaverVoluntaryContribution,
            ]),
            caption: 'Your contributions',
          },
          employersContributions: {
            value: this.getAccountsBalancesValue(performanceBalancesData, [
              AccountSubType.KiwiSaverEmployerContribution,
            ]),
            caption: "Employer's contributions",
          },
          governmentContributions: {
            value: this.getAccountsBalancesValue(performanceBalancesData, [
              AccountSubType.KiwiSaverGovernmentContribution,
            ]),
            caption: 'Government contributions',
          },
          otherContributions: {
            value: this.getAccountsBalancesValue(performanceBalancesData, [
              AccountSubType.KiwiSaverAUSSuperContribution,
              AccountSubType.KiwiSaverOtherSuperContribution,
            ]),
            caption: 'Other contributions',
          },
          withdrawals: {
            // when a withdrawal AccountSubtype is added on the backend, add it here as well
            value: this.getAccountsBalancesValue(performanceBalancesData, [
              AccountSubType.CapitalRedeemed,
              AccountSubType.CapitalDistributed,
              AccountSubType.CashSweep,
              AccountSubType.KiwiSaverAdminError,
              AccountSubType.KiwiSaverCourtOrder,
              AccountSubType.KiwiSaverDD_Dishonour,
              AccountSubType.KiwiSaverDeath,
              AccountSubType.KiwiSaverFinancialHardship,
              AccountSubType.KiwiSaverFirstHomePurchase,
              AccountSubType.KiwiSaverInvalidEnrolment,
              AccountSubType.KiwiSaverIRDRefund,
              AccountSubType.KiwiSaverLateOptOut,
              AccountSubType.KiwiSaverMortgageDiversion,
              AccountSubType.KiwiSaverEmigrationAUS,
              AccountSubType.KiwiSaverEmigrationElsewhere,
              AccountSubType.KiwiSaverRetirement,
              AccountSubType.KiwiSaverSeriousIllness,
              AccountSubType.KiwiSaverTransferOut,
            ]),
            caption: 'Withdrawals',
          },
          returns: {
            value: this.getTotalReturnsValue(data.performanceData.snapshots),
            caption: 'Gains / (losses) after fees',
          },
          tax: {
            value: this.getAccountsBalancesValue(performanceBalancesData, [
              AccountSubType.TaxPaid,
              AccountSubType.TaxWithheld,
            ]),
            caption: 'Tax refund / (paid)',
          },
        };
      return of(kiwiSaverDashboardSummaryWidgetData);
    })
  );

  getPerformanceState() {
    return this.store.pipe(select(selectPerformanceState));
  }

  /**
   * Retrieves only the performance data, loading status and error status from the performance state
   */
  getPerformanceDataLoadingStatusAndErrorStatus() {
    return this.store.pipe(select(selectPerformanceDataErrorLoading));
  }


  getAccountsBalancesValue(
    flattenedBreakDownData: IPerformanceBalancesObject[],
    accountSubTypes: AccountSubType[]
  ): number {
    return flattenedBreakDownData
      .filter((d: IPerformanceBalancesObject) => {
        return accountSubTypes.includes(d.accountSubType);
      })
      .map((e: IPerformanceBalancesObject) => e.value)
      .reduce((prev, current) => prev + current, 0);
  }

  getTotalReturnsValue(snapshotData: IPerformanceSnapshotObject[]): number {
    // TODO: #dateselector need to revisit when dateselector gets implemented, for now since inception
    let totalReturnsValue = 0;
    totalReturnsValue += snapshotData
      .filter(s => s.caption === 'Inception')
      .map(d => d.returnInCurrency)
      .reduce((prev, next) => prev + next, 0);
    return totalReturnsValue;
  }

  constructor(private store: Store<IAppState>, private appConfig: AppConfig, private generalSelectionFacade: GeneralSelectionFacade) {
    // Ensure the performance configuration from initial store state
    this.clearPerformanceState();

    this.subscribeToGeneralSelectionState();
    this.subscribeToPerformanceState();
  }

  /**
 * Subscibre to the general selection state. This data is required for when we dispatch a call to update performance data.
 */
  private subscribeToGeneralSelectionState(): void {
    this.generalSelectionFacade
      .getState()
      .pipe(untilDestroyed(this))
      .subscribe((state: IGeneralSelectionState) => {
        this.selectedDate = state.toDate;
        this.selectedEntities = state.selectedEntities;
      });
  }

  /**
   * Subscibre to the performance state. This data is required for when we dispatch a call to update performance data.
   */
  private subscribeToPerformanceState() {
    this.getPerformanceSettings()
      .pipe(untilDestroyed(this))
      .subscribe((settings: PerformanceSettings) => {
        this.selectedReturnSpanMonths = settings.selectedReturnSpanMonths;
        this.selectedReturnPeriod = settings.selectedReturnPeriod;
        this.selectedReturnType = settings.selectedReturnType;
      });
  }

  getPerformanceData(params: IPerformance) {
    this.store.dispatch(loadPerformanceData({ payload: params }));
  }

  getAssetPerformanceData(params: AssetPerformanceRequest) {
    this.store.dispatch(loadAssetPerformanceData({ payload: params }));
  }

  getAssetClassificationPerformanceData(params: AssetPerformanceRequest) {
    this.store.dispatch(loadAssetClassificationPerformanceData({ payload: params }));
  }

  /**
  * Updates the performance setting values in state and triggers a reload of performance data.
  */
  updatePerformanceSettings(settings: PerformanceSettings) {
    this.store.dispatch(updatePerformanceSettings({ settings }));
  }

  /**
   * Updates the performance setting values in state without triggering a reload of performance data.
   */
  updatePerformanceSettingsWithoutReload(settings: PerformanceSettings) {
    this.store.dispatch(updatePerformanceSettingsWithoutReload({ settings }));
  }

  getPerformanceClosingValue(){
    return this.store.pipe(select(selectePerformanceClosingValue));
  }

  getPerformanceStateLoading(){
    return this.store.pipe(select(selectPerformanceStateLoading));
  }

  getPerformanceInceptionData(){
    return this.store.pipe(select(selectePerformanceInceptionDates));
  }

  getPerformanceSettings() {
    return this.store.pipe(select(selectPerformanceSettings));
  }

  clearPerformanceState() {
    const performanceFeature = this.appConfig.getSection(Features.PERFORMANCE) as IPerformanceFeatureSettings;
    const performanceSpanMonths = Number(performanceFeature['initialSpanMonths']);

    //Ensures when clearing the state we get the default return symbol from the config if it is set
    const returnSymbol = this.getDefaultReturnSymbol(performanceFeature);

    //Reset all of the performance settings in state back to their default values
    let settings = {
      selectedReturnSpanMonths: performanceSpanMonths,
      selectedReturnPeriod: DateSelectorCaption.MONTH,
      selectedReturnType: ReturnTypes.XIRR,
      selectedReturnSymbol: returnSymbol
    } as PerformanceSettings;

    this.store.dispatch(
      clearPerformanceState({settings})
    );
  }

  /**
   * Behaviour subject to represent the selected return symbol in the performance widget.
   * The selected value affects other widgets so when it changes the updated value needs to be accessible to other widgets.
   */
  public updateReturnsSymbol(returnsSybmbol: string) {
    this._performanceReturnSymbol$.next(returnsSybmbol);
  }
  private _performanceReturnSymbol$: BehaviorSubject<string> = new BehaviorSubject<string>(ReturnSymbol.PERCENT);
  public performanceReturnSymbol$: Observable<string> = this._performanceReturnSymbol$.asObservable();

  /**
   * Behaviour subject to represent the selected display period in the performance widget.
   * The selected value affects other widgets so when it changes the updated value needs to be accessible to other widgets.
   */
  public updatePerfomanceDisplay(performanceDisplay: string) : void{
    this._performanceDisplayValue$.next(performanceDisplay)
  }

  private _performanceDisplayValue$: BehaviorSubject<string> = new BehaviorSubject<string>(DateSelectorCaption.YEAR);
  public performanceDisplayValue$: Observable<string> = this._performanceDisplayValue$.asObservable();

  private getDefaultReturnSymbol(performanceFeature : IPerformanceFeatureSettings) : ReturnSymbol{
    const defaultPerformanceSymbol = performanceFeature?.defaultReturnSymbol ?? ReturnSymbol.PERCENT;
    return defaultPerformanceSymbol;
  }

  public onDisplayOptionChange(displayOption: number) : void {
    const paramsObject: IPerformance = {
      clientEntities: this.selectedEntities,
      date: this.selectedDate,
      returnType: this.selectedReturnType,
      returnPeriod: this.selectedReturnPeriod,
      returnSpanMonths: displayOption,
    };
    this.getPerformanceData(paramsObject);
    this.updatePerfomanceDisplay(this.getDateSelectorCaptionFromSpanMonths(displayOption));
  }

  private getDateSelectorCaptionFromSpanMonths(returnSpanMonths: number) : string {
      //Use the return span months to get the enum key
      const returnSpanKey = Object.keys(ReturnSpanMonths).find(key => ReturnSpanMonths[key] === returnSpanMonths);
      //Then use the key to get the date selector caption
      return returnSpanKey ? DateSelectorCaption[returnSpanKey] : '';
  }

  public onSegmentOptionChange(periodOption: string) : void {
    const paramsObject: IPerformance = {
      clientEntities: this.selectedEntities,
      date: this.selectedDate,
      returnType: this.selectedReturnType,
      returnPeriod: periodOption,
      returnSpanMonths: this.selectedReturnSpanMonths
    };
    this.getPerformanceData(paramsObject);
  }

  public onReturnTypeChange(returnType: string) : void {
    const paramsObject: IPerformance = {
      clientEntities: this.selectedEntities,
      date: this.selectedDate,
      returnType: returnType,
      returnPeriod: this.selectedReturnPeriod,
      returnSpanMonths: this.selectedReturnSpanMonths
    };
    this.getPerformanceData(paramsObject);
  }
}
